import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Loader2, Plus, Save, X } from "lucide-react";
import type {
  RequisitoLegalAspecto,
  RequisitoLegalPayload,
  RequisitoLegalRow,
} from "../types/requisitos-legales";
import { createEmptyRequisitoLegalPayload } from "../types/requisitos-legales";

type Props = {
  open: boolean;
  mode: "create" | "edit";
  aspectos: RequisitoLegalAspecto[];
  rows: RequisitoLegalRow[];
  initialValues: RequisitoLegalPayload;
  submitting: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (payload: RequisitoLegalPayload) => void;
};

export function RequisitoLegalFormModal({
  open,
  mode,
  aspectos,
  rows,
  initialValues,
  submitting,
  error,
  onClose,
  onSubmit,
}: Props) {
  const [values, setValues] = useState<RequisitoLegalPayload>(
    createEmptyRequisitoLegalPayload(),
  );

  useEffect(() => {
    if (open) {
      setValues(initialValues);
    }
  }, [initialValues, open]);

  if (!open) {
    return null;
  }

  const referencias = Array.from(
    new Map(rows.map((row) => [row.referencia_id, row])).values(),
  ).sort((a, b) => a.referencia.localeCompare(b.referencia));

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit({
      ...values,
      aspecto_nombre: values.aspecto_nombre.trim(),
      referencia: values.referencia.trim(),
      observaciones: normalizeString(values.observaciones),
      requisito: normalizeString(values.requisito),
      periodicidad: normalizeString(values.periodicidad),
      registro_a_obtener: normalizeString(values.registro_a_obtener),
      ambito_original: normalizeString(values.ambito_original),
      derogada_original: normalizeString(values.derogada_original),
      cumplimiento_original: normalizeString(values.cumplimiento_original),
    });
  };

  const selectedReferencia = referencias.find(
    (referencia) => referencia.referencia_id === values.referencia_id,
  );

  return (
    <div className="reqleg-modal-backdrop" role="dialog" aria-modal="true">
      <form className="reqleg-form-modal" onSubmit={handleSubmit}>
        <div className="reqleg-modal-header">
          <div>
            <div className="calidad-section-eyebrow">
              {mode === "create" ? "Nuevo registro" : "Editar registro"}
            </div>
            <h3 className="reqleg-modal-title">Requisito legal</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="reqleg-modal-close"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        {error ? <div className="reqleg-inline-error">{error}</div> : null}

        <div className="reqleg-form-grid">
          <label className="reqleg-field reqleg-field-span-2">
            <span>Aspecto</span>
            <input
              list="reqleg-aspectos"
              value={values.aspecto_nombre}
              onChange={(event) => {
                const name = event.target.value;
                const aspecto = aspectos.find((item) => item.nombre === name);
                setValues((current) => ({
                  ...current,
                  aspecto_nombre: name,
                  aspecto_id: aspecto?.id ?? null,
                  referencia_id: null,
                }));
              }}
              required
            />
            <datalist id="reqleg-aspectos">
              {aspectos.map((aspecto) => (
                <option key={aspecto.id} value={aspecto.nombre} />
              ))}
            </datalist>
          </label>

          <label className="reqleg-field reqleg-field-span-2">
            <span>Referencia</span>
            <input
              list="reqleg-referencias"
              value={values.referencia}
              onChange={(event) => {
                const referenciaText = event.target.value;
                const referencia = referencias.find(
                  (item) => item.referencia === referenciaText,
                );
                setValues((current) => ({
                  ...current,
                  referencia: referenciaText,
                  referencia_id: referencia?.referencia_id ?? null,
                  aspecto_id: referencia?.aspecto_id ?? current.aspecto_id,
                  aspecto_nombre: referencia?.aspecto ?? current.aspecto_nombre,
                  ambito: referencia?.ambito ?? current.ambito,
                  ambito_original: referencia?.ambito_original ?? current.ambito_original,
                  derogada: referencia?.derogada ?? current.derogada,
                  derogada_original: referencia?.derogada_original ?? current.derogada_original,
                  observaciones: referencia?.observaciones ?? current.observaciones,
                }));
              }}
              required
            />
            <datalist id="reqleg-referencias">
              {referencias.map((referencia) => (
                <option key={referencia.referencia_id} value={referencia.referencia} />
              ))}
            </datalist>
          </label>

          <label className="reqleg-field">
            <span>Ámbito</span>
            <select
              value={values.ambito}
              onChange={(event) =>
                setValues((current) => ({ ...current, ambito: event.target.value }))
              }
            >
              <option value="europea">Europea</option>
              <option value="estatal">Estatal</option>
              <option value="autonomica">Autonómica</option>
              <option value="local">Local</option>
              <option value="otro">Otro</option>
            </select>
          </label>

          <label className="reqleg-field">
            <span>Derogada</span>
            <select
              value={values.derogada ? "si" : "no"}
              onChange={(event) =>
                setValues((current) => ({
                  ...current,
                  derogada: event.target.value === "si",
                }))
              }
            >
              <option value="no">No</option>
              <option value="si">Sí</option>
            </select>
          </label>

          {values.ambito === "otro" ? (
            <label className="reqleg-field reqleg-field-span-2">
              <span>Ámbito original</span>
              <input
                value={values.ambito_original ?? ""}
                onChange={(event) =>
                  setValues((current) => ({
                    ...current,
                    ambito_original: event.target.value,
                  }))
                }
              />
            </label>
          ) : null}

          <label className="reqleg-field reqleg-field-span-2">
            <span>Observaciones</span>
            <textarea
              value={values.observaciones ?? ""}
              onChange={(event) =>
                setValues((current) => ({ ...current, observaciones: event.target.value }))
              }
              rows={3}
            />
          </label>

          <div className="reqleg-form-divider">
            <strong>Requisito asociado</strong>
            {selectedReferencia && mode === "create" ? (
              <span>Se añadirá a la referencia seleccionada.</span>
            ) : null}
          </div>

          <label className="reqleg-field reqleg-field-span-2">
            <span>Requisito</span>
            <textarea
              value={values.requisito ?? ""}
              onChange={(event) =>
                setValues((current) => ({ ...current, requisito: event.target.value }))
              }
              rows={4}
            />
          </label>

          <label className="reqleg-field">
            <span>Periodicidad</span>
            <textarea
              value={values.periodicidad ?? ""}
              onChange={(event) =>
                setValues((current) => ({ ...current, periodicidad: event.target.value }))
              }
              rows={3}
            />
          </label>

          <label className="reqleg-field">
            <span>Registro a obtener</span>
            <textarea
              value={values.registro_a_obtener ?? ""}
              onChange={(event) =>
                setValues((current) => ({
                  ...current,
                  registro_a_obtener: event.target.value,
                }))
              }
              rows={3}
            />
          </label>

          <label className="reqleg-field">
            <span>Cumplimiento</span>
            <select
              value={
                values.cumplimiento === null
                  ? "pendiente"
                  : values.cumplimiento
                    ? "si"
                    : "no"
              }
              onChange={(event) =>
                setValues((current) => ({
                  ...current,
                  cumplimiento:
                    event.target.value === "pendiente"
                      ? null
                      : event.target.value === "si",
                }))
              }
            >
              <option value="pendiente">Pendiente</option>
              <option value="si">Sí</option>
              <option value="no">No</option>
            </select>
          </label>
        </div>

        <div className="reqleg-modal-actions">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="calidad-back-button"
          >
            Cancelar
          </button>
          <button type="submit" disabled={submitting} className="calidad-primary-button">
            {submitting ? <Loader2 size={16} className="animate-spin" /> : mode === "create" ? <Plus size={16} /> : <Save size={16} />}
            {mode === "create" ? "Crear registro" : "Guardar cambios"}
          </button>
        </div>
      </form>
    </div>
  );
}

function normalizeString(value: string | null): string | null {
  const trimmed = value?.trim() ?? "";
  return trimmed === "" ? null : trimmed;
}
